<template>
  <div>
    <!-- Capacity Summary -->
    <div class="mb-2">
      <div class="accordion" id="coachDetailsAccordion">
        <AccordionItem id="coachDetailsCollapse" title="Capacity Summary" parent-id="coachDetailsAccordion">
          <div v-if="coachData" class="ms-2">
            <div class="d-flex justify-content-between mb-1">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span>{{ __DICT__.capacity }}:</span>
              <span>{{ coachData.Capacity }}</span>
            </div>
            <div
                v-for="group in passengerGroups"
                :key="group.brand"
                class="d-flex justify-content-between mb-1"
            >
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span>{{ group.brand }} {{ __DICT__.passengers }}:</span>
              <span>{{ group.count }}</span>
            </div>
            <div v-if="staffCount > 0" class="d-flex justify-content-between mb-1">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span>{{ __DICT__.loadTransferView.repsAndWelfare }}:</span>
              <span>{{ staffCount }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span>{{ __DICT__.loadTransferView.spareSeats }}:</span>
              <span>{{ spareSeats }}</span>
            </div>
          </div>
          <div v-else class="text-muted small p-2">
            {{ __DICT__.transferInfo.capacitySummaryNotAvailable }}
          </div>
        </AccordionItem>
      </div>
    </div>

    <!-- Pick Up Locations Section -->
    <div class="mb-2">
      <div class="accordion" id="coachPickupAccordion">
        <AccordionItem id="coachPickupCollapse" title="Scanning progress by pick up" parent-id="coachPickupAccordion">
          <div class="mb-3" v-for="p in passengers" :key="`pickup-${p.location}`">
            <div class="accordion" :id="`coach-pickup-location-${sanitizeForSelector(p.location)}`">
              <AccordionItem
                  :id="`coach-pickup-collapse-${sanitizeForSelector(p.location)}`"
                  :parent-id="`coach-pickup-location-${sanitizeForSelector(p.location)}`"
              >
                <template #header>
                  <div class="w-100 pe-2">
                    <ProgressBar
                        :percent="(p.loadedPassengers.length / (p.loadedPassengers.length + p.remainingPassengers.length)) * 100"
                        :total="p.loadedPassengers.length + p.remainingPassengers.length"
                        :raw="p.loadedPassengers.length"
                        :heading="p.location"
                    />
                  </div>
                </template>
                <div class="accordion" :id="`coach-pickup-status-${sanitizeForSelector(p.location)}`">
                  <AccordionItem
                      :id="`coach-pickup-remaining-${sanitizeForSelector(p.location)}`"
                      :parent-id="`coach-pickup-status-${sanitizeForSelector(p.location)}`"
                      :title="`Remaining Passengers (${p.remainingPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.remainingPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        <Badge v-if="passenger.equipment" text="E" pill/>
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'black', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                  <AccordionItem
                      :id="`coach-pickup-loaded-${sanitizeForSelector(p.location)}`"
                      :parent-id="`coach-pickup-status-${sanitizeForSelector(p.location)}`"
                      :title="`Loaded Passengers (${p.loadedPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.loadedPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        <Badge v-if="passenger.equipment" text="E" pill/>
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'grey-500', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                </div>
              </AccordionItem>
            </div>
          </div>
        </AccordionItem>
      </div>
    </div>

    <!-- Expected Passengers -->
    <div class="mb-3">
      <div class="accordion" id="coachMainAccordion">
        <AccordionItem id="coachMainCollapse" parent-id="coachMainAccordion">
          <template #header>
            {{ __DICT__.expected }} {{ __DICT__.passengers }} <br/>
            <div class="w-100 pe-2">
              <ProgressBar
                  :percent="(ticketData.loaded/(remainingPassengers.length + loadedPassengers.length))*100"
                  :total="remainingPassengers.length + loadedPassengers.length"
                  :raw="ticketData.loaded"
                  heading="Loaded"
              />
            </div>
          </template>
          <Card class="mb-3" :title="`Remaining Passengers (${remainingPassengers.length})`" :title-level="3" margin="">
            <ul class="list-group list-group-flush">
              <li v-for="passenger in remainingPassengers"
                  :key="passenger.bookingRef || passenger.reference"
                  class="list-group-item d-flex justify-content-between"
                  @click="$emit('openPassengerQuickView', passenger)"
              >
                <Badge v-if="passenger.equipment" text="E" pill/>
                {{ passenger.name || passenger.passengerName }}
                <Badge
                    :text="(passenger.bookingRef || passenger.reference)?.split('-')[0]"
                    :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'black', 'P24': 'purple-lt' }[(passenger.bookingRef || passenger.reference)?.split('-')[0]] ?? 'orange'"
                    class="ms-auto"
                />
              </li>
            </ul>
          </Card>
          <Card class="mb-3" :title="`Loaded Passengers (${loadedPassengers.length})`" :title-level="3" margin="">
            <ul class="list-group list-group-flush">
              <li v-for="passenger in loadedPassengers"
                  :key="passenger.bookingRef || passenger.reference"
                  class="list-group-item d-flex justify-content-between"
                  @click="$emit('openPassengerQuickView', passenger)"
              >
                <Badge v-if="passenger.equipment" text="E" pill/>
                {{ passenger.name || passenger.passengerName }}
                <Badge
                    :text="(passenger.bookingRef || passenger.reference)?.split('-')[0]"
                    :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'grey-500', 'P24': 'purple-lt' }[(passenger.bookingRef || passenger.reference)?.split('-')[0]] ?? 'orange'"
                    class="ms-auto"
                />
              </li>
            </ul>
          </Card>
        </AccordionItem>
      </div>
    </div>

    <!-- Reps & Welfare -->
    <div class="accordion" id="coachRepsAccordion">
      <AccordionItem :title="`Reps & Welfare (${reps.length})`" show-chevron>
        <div class="list-group list-group-flush">
          <div @click="$emit('openRepQuickView', rep)" v-for="rep in reps" :key="rep['GBS ID'] || rep.Name"
               class="list-group-item list-group-item-action d-flex justify-content-between">
            {{ rep.Name }}<br/>
            <span class="small">{{ repPickup(rep) || rep.Resort }}</span>
          </div>
        </div>
      </AccordionItem>
    </div>

    <!-- Drop Off Locations Section -->
    <div class="mt-2" v-if="dropOffPassengers.length > 0">
      <div class="accordion" id="coachDropoffAccordion">
        <AccordionItem id="coachDropoffCollapse" :title="`${__DICT__.passenger} drop off`" parent-id="coachDropoffAccordion">
          <div class="mb-3" v-for="p in dropOffPassengers" :key="`dropoff-${p.location}`">
            <div class="accordion" :id="`coach-dropoff-location-${sanitizeForSelector(p.location)}`">
              <AccordionItem
                  :id="`coach-dropoff-collapse-${sanitizeForSelector(p.location)}`"
                  :parent-id="`coach-dropoff-location-${sanitizeForSelector(p.location)}`"
              >
                <template #header>
                  <div class="w-100 pe-2">
                    <ProgressBar
                        :percent="(p.loadedPassengers.length / (p.loadedPassengers.length + p.remainingPassengers.length)) * 100"
                        :total="p.loadedPassengers.length + p.remainingPassengers.length"
                        :raw="p.loadedPassengers.length"
                        :heading="p.location"
                    />
                  </div>
                </template>
                <div class="accordion" :id="`coach-dropoff-status-${sanitizeForSelector(p.location)}`">
                  <AccordionItem
                      :id="`coach-dropoff-remaining-${sanitizeForSelector(p.location)}`"
                      :parent-id="`coach-dropoff-status-${sanitizeForSelector(p.location)}`"
                      :title="`Remaining Passengers (${p.remainingPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.remainingPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'black', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                  <AccordionItem
                      :id="`coach-dropoff-loaded-${sanitizeForSelector(p.location)}`"
                      :parent-id="`coach-dropoff-status-${sanitizeForSelector(p.location)}`"
                      :title="`Loaded Passengers (${p.loadedPassengers.length})`"
                  >
                    <ul class="list-group list-group-flush">
                      <li v-for="passenger in p.loadedPassengers"
                          :key="passenger.reference"
                          class="list-group-item d-flex justify-content-between"
                          @click="$emit('openPassengerQuickView', passenger)"
                      >
                        {{ passenger.passengerName }}
                        <Badge
                            :text="passenger.reference?.split('-')[0]"
                            :variant="{ NUCO: 'red-lt', WASTELAND: 'blue-lt', WHITEROAD: 'grey-500', 'P24': 'purple-lt' }[passenger.reference?.split('-')[0]] ?? 'orange'"
                            class="ms-auto"
                        />
                      </li>
                    </ul>
                  </AccordionItem>
                </div>
              </AccordionItem>
            </div>
          </div>
        </AccordionItem>
      </div>
    </div>

    <div style="height: 100px"></div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ProgressBar from "@/components/ProgressBar.vue";
import {AccordionItem, Badge, Card} from "@/components/atomic";
import {sanitizeForSelector} from "@/utils/cssUtils.js";

const props = defineProps({
  direction: { type: String, required: true },
  coachData: { type: Object, default: null },
  passengers: { type: Array, default: () => [] },
  remainingPassengers: { type: Array, default: () => [] },
  loadedPassengers: { type: Array, default: () => [] },
  dropOffPassengers: { type: Array, default: () => [] },
  ticketData: { type: Object, default: () => ({}) },
  reps: { type: Array, default: () => [] },
});

defineEmits(['openPassengerQuickView', 'openRepQuickView']);

const staffCount = computed(() => props.reps.length);

const passengerGroups = computed(() => {
  if (!props.ticketData?.tickets) return [];

  const groups = {};
  for (const ticket of props.ticketData.tickets) {
    if (ticket.deleted === 1) continue;
    const brand = (ticket.bookingRef || '').split('-')[0];
    if (!brand) continue;
    if (!groups[brand]) groups[brand] = { brand, count: 0 };
    groups[brand].count++;
  }
  return Object.values(groups);
});

const spareSeats = computed(() => {
  if (!props.coachData?.Capacity) return 'N/A';

  const capacity = parseInt(props.coachData.Capacity) || 0;
  const totalTickets = props.remainingPassengers.length + props.loadedPassengers.length;
  const staffPrefix = props.direction === 'outbound' ? 'OB' : 'IB';
  const nucoStaff = parseInt(props.coachData[`${staffPrefix} NUCO Staff`]) || 0;
  const wltStaff = parseInt(props.coachData[`${staffPrefix} WLT Staff`]) || 0;

  return capacity - (totalTickets + nucoStaff + wltStaff);
});

const repPickup = (rep) => {
  if (props.direction === 'outbound') {
    return rep['OB Pickup Point']
  } else {
    return rep['IB Pickup Point']
  }
};
</script>
